import { useState, useEffect, useRef } from "react";
import { useStudioErstellen } from "../../../store/studioErstellenStore";

import ErrorMessage from "../ErrorMessage";
import { NotificationModal } from "./NotificationModal";
import EquipmentBoxPhone from "./EquipmentBoxPhone";
import classes from "./StudioEquipment.module.css";

const EquipmentBox = ({ name, validate }) => {
    const { studioData, updateStudioData } = useStudioErstellen();
    const [ showModal, setShowModal ] = useState(false);
    const [ pendingChecked, setPendingChecked ] = useState(false);
    const [ isActive, setIsActive ] = useState(() => {
        return !!(studioData.equipment && 
                 studioData.equipment[name] && 
                 studioData.equipment[name].length > 0);
    });
    const [isExpanded, setIsExpanded] = useState(false);
    const hasInitialized = useRef(false);

    const [equipmentItems, setEquipmentItems] = useState([{ name: '', count: 1, id: Date.now() }]);
    const [errors, setErrors] = useState({
        name: ""
    });



    const handleDeleteAll = () => {
        setEquipmentItems([]);
        updateStudioData('equipment', {
            ...studioData.equipment,
            [name]: []
        });
        setShowModal(false);
        setIsActive(false);
    }

    const handleValidation = (items, currentId, newName) => {
        if (!isActive) {
            return true;
        }
    
        let isValid = true;
        const newErrors = {
            name: ""
        };
    
        if (items.length > 0) {
            if (newName.trim() === "") {
                newErrors.name = "Name muss ausgefüllt sein.";
                isValid = false;
            }
    
            const isDuplicate = items.some(item => 
                item.id !== currentId && 
                item.name.trim().toLowerCase() === newName.trim().toLowerCase()
            );
    
            if (isDuplicate) {
                newErrors.name = "Name schon vorhanden.";
                isValid = false;
            }
        }
    
        setErrors(newErrors);
        return isValid;
    };

    useEffect(() => {
        
        // Only update from store on initial load, not on subsequent store updates
        if (!hasInitialized.current && studioData.equipment && studioData.equipment[name]) {
            const categoryItems = studioData.equipment[name];
            if (categoryItems.length > 0) {
                setEquipmentItems(categoryItems);
                setIsActive(true);
            }
            hasInitialized.current = true;
        }
    }, [studioData.equipment, name]);

    useEffect(() => {
        if (equipmentItems.length === 0) {
            setIsActive(false);
        }
    }, [equipmentItems]);

    const handleUpdateItem = (id, field, value) => {
        if (field === 'name') {
            const updatedItems = equipmentItems?.map(item => 
                item.id === id ? { ...item, [field]: value } : item
            );

            // Clear errors when field is empty and not active
            if (!isActive || value.trim() === '') {
                setErrors({ name: '' });
            } else {
                const isValid = handleValidation(updatedItems, id, value);
                if (!isValid) {
                    setEquipmentItems(updatedItems);
                    return;
                }
                setErrors({ name: '' });
            }
    
            // Always update the UI state to keep input fields visible
            setEquipmentItems(updatedItems);
            
            // Only save non-empty items to the store, but keep all items in UI
            const itemsToSave = updatedItems?.filter(item => item.name.trim() !== '');

            if (itemsToSave?.length > 0) {
                updateStudioData('equipment', {
                    ...studioData.equipment,
                    [name]: itemsToSave
                });
            } else {
                // If no valid items to save, remove the category from store but keep UI items
                const newEquipment = { ...studioData.equipment };
                delete newEquipment[name];
                updateStudioData('equipment', newEquipment);
            }
        } else {
            const updatedItems = equipmentItems.map(item => 
                item.id === id ? { ...item, [field]: value } : item
            );
            setEquipmentItems(updatedItems);
            
            // Only save items with non-empty names to the store
            const itemsToSave = updatedItems?.filter(item => item.name.trim() !== '');
            
            if (itemsToSave?.length > 0) {
                updateStudioData('equipment', {
                    ...studioData.equipment,
                    [name]: itemsToSave
                });
            } else {
                // If no valid items to save, remove the category from store but keep UI items
                const newEquipment = { ...studioData.equipment };
                delete newEquipment[name];
                updateStudioData('equipment', newEquipment);
            }
        }
    };

    const handleAddItem = () => {
        setIsExpanded(true);
        const newItem = { name: '', count: 1, id: Date.now() };
        setEquipmentItems([...equipmentItems, newItem]);
    };

    const handleDeleteItem = (id) => {
        const newItems = equipmentItems.filter(item => item.id !== id);
        setEquipmentItems(newItems);
        
        // Clear errors if the deleted item had an empty name
        if (equipmentItems.find(item => item.id === id)?.name.trim() === '') {
            setErrors({ name: '' });
        }

        // Save only non-empty items to the store
        const itemsToSave = newItems.filter(item => item.name.trim() !== '');
        if (itemsToSave.length > 0) {
            updateStudioData('equipment', {
                ...studioData.equipment,
                [name]: itemsToSave
            });
        } else {
            // Remove category from store if no valid items remain
            const newEquipment = { ...studioData.equipment };
            delete newEquipment[name];
            updateStudioData('equipment', newEquipment);
        }

        // Only collapse if no items remain
        if (newItems.length === 0) {
            setIsExpanded(false);
        }
    };

    const handleToggleActive = (checked) => {
        if (!checked) {
            if (equipmentItems.some(item => item.name.trim() !== '')) {
                setShowModal(true);
                setPendingChecked(checked);
                return;
            }
        }
        
        setIsActive(checked);
        setIsExpanded(checked);
        setErrors({ name: '' });
    
        if (!checked) {
            setIsExpanded(false);
            setEquipmentItems([]);
            updateStudioData('equipment', {
                ...studioData.equipment,
                [name]: []
            });
        } else {
            setEquipmentItems([{ name: '', count: 1, id: Date.now() }]);
        }
    };

    const handleModalConfirm = () => {
        setShowModal(false);
        setIsActive(false);
        setEquipmentItems([]);
        updateStudioData('equipment', {
            ...studioData.equipment,
            [name]: []
        });
    };

    const handleModalCancel = () => {
        setShowModal(false);
        setIsActive(true);
    };

    useEffect(() => {
        if (validate) {
            validate.current = () => {
                // Only validate if the category is active and has items
                if (!isActive) {
                    return true;
                }
                
                // Check if there are any non-empty items
                const hasItems = equipmentItems.some(item => item.name.trim() !== '');
                
                // If active but no items, it's valid
                if (!hasItems) {
                    return true;
                }
    
                // Validate all items
                let isValid = true;
                const newErrors = { name: '' };
    
                // Check for empty names and duplicates
                equipmentItems.forEach(item => {
                    if (item.name.trim() === '') {
                        newErrors.name = "Name muss ausgefüllt sein.";
                        isValid = false;
                    }
    
                    const duplicates = equipmentItems.filter(
                        otherItem => 
                            otherItem.id !== item.id && 
                            otherItem.name.trim().toLowerCase() === item.name.trim().toLowerCase()
                    );
                    if (duplicates.length > 0) {
                        newErrors.name = "Name schon vorhanden.";
                        isValid = false;
                    }
                });
    
                setErrors(newErrors);
                return isValid;
            };
        }
    }, [validate, equipmentItems, isActive]);

    return (
        <div className={`${classes.equipmentBox} ${isActive ? classes.active : ''}`}>
            <div className={classes.header}>
                <span className="semiboldLarge">{name}</span>
                <div className={classes.switchContainer}>
                    <label className={classes.switch}>
                        <input 
                            type="checkbox" 
                            checked={isActive}
                            onChange={(e) => handleToggleActive(e.target.checked)}
                        />
                        <span className={classes.slider}></span>
                    </label>
                </div>
            </div>
            <div className={classes.expandableContent}>
                {isActive && (
                    <>
                        {equipmentItems?.map(item => (
                            <div key={item.id} className={classes.equipmentItem}>
                                <div className={classes.insideInputContainer}>
                                    <input
                                        type="text"
                                        value={item?.name}
                                        onChange={(e) => handleUpdateItem(item?.id, 'name', e.target.value)}
                                        placeholder="Name/Modell eingeben"
                                        className={classes.nameInput}
                                    />
                                    <div className={classes.countControl}>
                                        <button 
                                            onClick={() => handleUpdateItem(item?.id, 'count', Math.max(1, item?.count - 1))}
                                            className={classes.countButton}
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" width="13" height="2" viewBox="0 0 13 2" fill="none">
                                                <path d="M0.196289 1.75V0.25H12.1963V1.75H0.196289Z" fill={`${item?.count === 1 ? '#DEE1E5' : '#1F1F1F'}`}/>
                                            </svg>
                                        </button>
                                        <span className={classes.count}>{item?.count}</span>
                                        <button 
                                            onClick={() => handleUpdateItem(item?.id, 'count', item?.count + 1)}
                                            className={classes.countButton}
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" width="13" height="12" viewBox="0 0 13 12" fill="none">
                                                <path d="M5.73283 12V0H7.25283V12H5.73283ZM0.479492 6.77333V5.25333H12.4795V6.77333H0.479492Z" fill="#1F1F1F"/>
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                                <button 
                                    className={classes.deleteButton}
                                    onClick={() => handleDeleteItem(item.id)}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
                                        <path fillRule="evenodd" clipRule="evenodd" d="M7.2689 8.72266L0.201172 1.65492L1.13344 0.722656L8.20117 7.79039L15.2689 0.722656L16.2012 1.65492L9.13344 8.72266L16.2012 15.7904L15.2689 16.7227L8.20117 9.65493L1.13344 16.7227L0.201172 15.7904L7.2689 8.72266Z" fill="#696F76"/>
                                    </svg>
                                </button>
                            </div>
                        ))}
                        {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}        
                        <button 
                            className={`${classes.addMoreButton} semiboldXs ${
                                (!equipmentItems[equipmentItems.length - 1]?.name.trim() || 
                                errors.name) ? classes.disabled : ''
                            }`}
                            disabled={!equipmentItems[equipmentItems.length - 1]?.name.trim() || 
                                    errors.name}
                            onClick={handleAddItem}
                        >
                            Weiteres {name} hinzufügen
                        </button>
                        <NotificationModal 
                            open={showModal}
                            onBackdropClick={() => handleModalCancel()}
                            onConfirm={handleModalConfirm}
                            onCancel={handleModalCancel}
                            onDelete={handleDeleteAll}
                        >
                            {name}
                        </NotificationModal>
                    </>
                )}
            </div>
        </div>
    );
};

export const StudioEquipment = ({ validate, fromEditor }) => {
    const { studioData } = useStudioErstellen();
    
    const getEquipmentCategories = () => {
        const types = Array.isArray(studioData?.studioTypes) ? studioData.studioTypes : [];
        const hasMusik = types.includes('Musik');
        const hasFotoOrVideo = types.includes('Foto') || types.includes('Video');
        
        const photoVideoCategories = [
            "Lichtequipment",
            "Kameras und Objektive",
            "Stative",
            "Set-Accessoires",
            "Weiteres Equipment"
        ];
        
        const musikCategories = [
            "Aufnahme & Mikrofone",
            "Lautsprecher & Kopfhörer (Monitoring)",
            "Recorder",
            "Software & Computer",
            "Mischpulte & Steuerung",
            "Klangbearbeitung (Preamps & Effekte)",
            "Instrumente",
            "Akustik & Kabel",
            "Sonstiges Equipment"
        ];
        
        if (hasMusik && !hasFotoOrVideo) {
            // Only Musik selected
            return musikCategories;
        } else if (hasMusik && hasFotoOrVideo) {
            // Both Musik and Foto/Video selected - show both
            return [...photoVideoCategories, ...musikCategories];
        } else {
            // Only Foto/Video or none selected
            return photoVideoCategories;
        }
    };
    
    const equipmentCategories = getEquipmentCategories();

  return (
    <div className={classes.mainContainer} style={{maxWidth: fromEditor && window.innerWidth < 742 ? '100%' : '429px'}}>
        <div className={classes.titleContainer}>
            <h2 style={{marginBottom: fromEditor ? '30px' : '0px'}} className={`${classes.title} titleSemibold`}>{fromEditor ? "Equipment" : "Studio-Equipment"}</h2>
            <span style={{display: fromEditor ? 'none' : 'block',}} className={`${classes.text} slimRegularLargeText`}>Kostenloses Equipment, das bei jeder Buchung verfügbar ist.</span>
        </div>
        <div className={classes.bottomContainer}>
            {equipmentCategories.map(category => (
                <EquipmentBox validate={validate} key={category} name={category} />
            ))}
        </div>
        <div className={classes.heavyLine}></div>
        <div className={classes.bottomContainerPhone}>
            {equipmentCategories.map(category => (
                <EquipmentBoxPhone validate={validate} key={category} name={category} />
            ))}
        </div>
    </div>
  );
};

export default StudioEquipment;